// ============================================================================
// CollectionsPage — Production-ready with all fixes:
//   1. Race condition fix: auto-select waits for fresh collections
//   2. Stale state fix: resets on mount (page navigation)
//   3. Theme title preserved in ref (not lost by clearGeneratedCollections)
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdChevronRight } from 'react-icons/md';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchCollectionsRequest,
  setSelectedItemId,
  fetchUserCollectionMediaRequest,
  clearGeneratedCollections,
} from '../../redux/slices/collectionsSlice';
import { APP_CONFIG } from '../../config/app.config';
import CollectionsSidebar from './CollectionsSidebar';
import CollectionDetailPanel from './CollectionDetailPanel';
import CollectionCreationPanel from './CollectionCreationPanel';
import CollectionGenerationLoader from './CollectionGenerationLoader';
import type { Collection } from '../../types/collections.types';

type RightPanel = 'detail' | 'create-ai' | 'create-manual' | 'generating';

const CollectionsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    collections,
    loading,
    selectedItemId,
    generating,
    addingBundles,
    themeTitle,
  } = useAppSelector((s) => s.collections);

  const [rightPanel, setRightPanel] = useState<RightPanel>('detail');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  // ── Refs for generation tracking ──
  const pendingAutoSelectRef = useRef(false);      // true while waiting for fresh collections
  const savedThemeTitleRef = useRef<string | null>(null); // preserved themeTitle
  const prevCollectionCountRef = useRef(0);

  // ── Pipeline active check ──
  const isGenerationActive = generating || addingBundles;

  // ═══════════════════════════════════════════════════════════════════════════
  // 1. MOUNT: Fresh fetch + reset stale state
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Reset local state on mount (fixes stale state when navigating back)
    setRightPanel('detail');
    pendingAutoSelectRef.current = false;
    savedThemeTitleRef.current = null;

    // Reset selection so first collection gets auto-selected after fetch
    dispatch(setSelectedItemId(null));

    // Fresh data fetch
    dispatch(fetchCollectionsRequest());
    dispatch(fetchUserCollectionMediaRequest({ userId: APP_CONFIG.MEDIA_USER_ID }));

    // Cleanup on unmount
    return () => {
      pendingAutoSelectRef.current = false;
      savedThemeTitleRef.current = null;
    };
  }, [dispatch]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 2. Switch to loader when generation starts + save themeTitle
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (isGenerationActive) {
      setRightPanel('generating');
      // Save themeTitle in ref BEFORE it gets cleared by clearGeneratedCollections
      if (themeTitle) {
        savedThemeTitleRef.current = themeTitle;
      }
      // Mark that we need to auto-select after collections refresh
      pendingAutoSelectRef.current = true;
    }
  }, [isGenerationActive, themeTitle]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 3. Auto-select AFTER fresh collections arrive (not after generating stops)
  //    Triggers when: loading transitions false AND pendingAutoSelect is true
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Only proceed when:
    // - We're waiting for auto-select
    // - Generation pipeline is fully done
    // - Collections have loaded (loading=false, collections available)
    if (!pendingAutoSelectRef.current) return;
    if (isGenerationActive) return;
    if (loading) return;

    // Collections have arrived — do the auto-select
    const title = savedThemeTitleRef.current;

    if (title && collections.length > 0) {
      // Find all collections matching the theme header (last = newest)
      const matched = collections.filter(
        (c) => c.header?.toLowerCase() === title.toLowerCase()
      );

      if (matched.length > 0) {
        const newest = matched[matched.length - 1];
        dispatch(setSelectedItemId(newest.pk_id));
        if (import.meta.env.DEV) {
          console.log('✅ Auto-selected:', newest.title, `(${matched.length} in theme "${title}")`);
        }
      } else {
        // Fallback: select the last collection (newest added)
        dispatch(setSelectedItemId(collections[collections.length - 1].pk_id));
      }
    } else if (collections.length > 0) {
      // No themeTitle but collections exist — select last
      dispatch(setSelectedItemId(collections[collections.length - 1].pk_id));
    }

    // Clean up
    pendingAutoSelectRef.current = false;
    savedThemeTitleRef.current = null;
    dispatch(clearGeneratedCollections());
    setRightPanel('detail');

    // Refresh media for new collections
    dispatch(fetchUserCollectionMediaRequest({ userId: APP_CONFIG.MEDIA_USER_ID }));
  }, [collections, loading, isGenerationActive, dispatch]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 4. Auto-select first collection on initial load (no generation pending)
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (
      !selectedItemId &&
      !pendingAutoSelectRef.current &&
      !isGenerationActive &&
      !loading &&
      collections.length > 0 &&
      rightPanel === 'detail'
    ) {
      dispatch(setSelectedItemId(collections[0].pk_id));
    }
  }, [collections, selectedItemId, loading, isGenerationActive, rightPanel, dispatch]);

  // ═══════════════════════════════════════════════════════════════════════════
  // Selected collection (memoized)
  // ═══════════════════════════════════════════════════════════════════════════
  const selectedCollection = useMemo<Collection | null>(
    () => collections.find((c) => c.pk_id === selectedItemId) ?? null,
    [collections, selectedItemId]
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // Handlers
  // ═══════════════════════════════════════════════════════════════════════════
  const handleSelectCollection = useCallback(
    (pkId: string) => {
      dispatch(setSelectedItemId(pkId));
      setRightPanel('detail');
    },
    [dispatch]
  );

  const handleCreateAI = useCallback(() => {
    setRightPanel('create-ai');
    dispatch(setSelectedItemId(null));
  }, [dispatch]);

  const handleCreateManual = useCallback(() => {
    setRightPanel('create-manual');
    dispatch(setSelectedItemId(null));
  }, [dispatch]);

  const handleCloseCreation = useCallback(() => {
    setRightPanel('detail');
    if (collections.length > 0) {
      dispatch(setSelectedItemId(collections[0].pk_id));
    }
  }, [collections, dispatch]);

  // ═══════════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="flex h-[calc(100vh-16px)] -mx-6 -my-2 lg:-mx-8 lg:-my-8 rounded-2xl overflow-hidden bg-[#121212]">
      {/* Sidebar */}
      <CollectionsSidebar
        collections={collections}
        selectedCollectionId={selectedItemId}
        onSelectCollection={handleSelectCollection}
        onCreateAI={handleCreateAI}
        onCreateManual={handleCreateManual}
        loading={loading}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* Right Panel */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* ── Global Sidebar Open Toggle — Vertical Tab (visible in ALL panel states) ── */}
        {!isSidebarOpen && (
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="absolute top-1 translate-y-1/2 left-0 z-30 flex flex-col items-center py-3 px-1 rounded-r-xl border border-l-0 border-white/[0.08] hover:border-white/[0.18] hover:px-2 transition-all duration-300 cursor-pointer group shadow-xl shadow-black/40 backdrop-blur-md"
            style={{ background: 'linear-gradient(180deg, rgba(30,30,30,0.95) 0%, rgba(20,20,20,0.98) 100%)' }}
            title="Open collections sidebar"
          >
            <MdChevronRight
              size={16}
              className="text-gray-500 group-hover:text-white transition-colors duration-200 mb-2"
            />
            <span
              className="text-[8px] font-bold text-gray-500 group-hover:text-gray-200 uppercase transition-colors duration-200"
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', letterSpacing: '0.15em' }}
            >
              Collections
            </span>
            <MdChevronRight
              size={16}
              className="text-gray-500 group-hover:text-white transition-colors duration-200 mt-2"
            />
          </button>
        )}

        {isGenerationActive || rightPanel === 'generating' ? (
          <div
            className="flex-1 overflow-y-auto bg-[#0f0f0f] ml-3"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <CollectionGenerationLoader isGenerating={isGenerationActive} isDarkMode />
          </div>
        ) : rightPanel === 'create-ai' ? (
          <div
            className="flex-1 overflow-y-auto bg-[#0f0f0f] ml-3"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
          <CollectionCreationPanel key="create-ai" onClose={handleCloseCreation} initialMode="ai" />
          </div>
        ) : rightPanel === 'create-manual' ? (
          <div
            className="flex-1 overflow-y-auto bg-[#0f0f0f] ml-3"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
          <CollectionCreationPanel key="create-manual" onClose={handleCloseCreation} initialMode="manual" />
          </div>
        ) : loading && !selectedCollection ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-[13px] text-gray-400">Loading collections...</p>
            </div>
          </div>
        ) : (
          <CollectionDetailPanel collection={selectedCollection} />
        )}
      </div>
    </div>
  );
};

CollectionsPage.displayName = 'CollectionsPage';
export default CollectionsPage;
